import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Modal } from 'react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import * as Yup from 'yup'
import { AddressElement } from '@stripe/react-stripe-js'
import CustomModal from 'components/CustomModal/CustomModal'
import styles from '../../PaymentModal.module.css'
import './BusinessDetailsModal.css'

const schema = Yup.object({
  vatId: Yup.string(),
  organization: Yup.string().required('Organization Name is required')
})

const BusinessDetailsModal = ({ show, onHide, onSave, initialValues = {}, firstName = '', lastName = '' }) => {
  const [addressComplete, setAddressComplete] = useState(false)
  const [addressError, setAddressError] = useState(null)
  const [addressOptions, setAddressOptions] = useState(null)
  const addressElementRef = useRef(null)

  useEffect(() => {
    if (show) {
      let defaultAddress = {
        country: 'US'
      }

      let defaultValues = {
        address: defaultAddress
      }

      const fullName = firstName && lastName ? `${firstName} ${lastName}`.trim() : ''
      if (fullName) {
        defaultValues.name = fullName
      }

      if (initialValues?.address) {
        if (initialValues.address.address) {
          defaultAddress = {
            ...initialValues.address.address,
            country: initialValues.address.address.country || 'US'
          }
          defaultValues.address = defaultAddress
        }
      }

      const options = {
        mode: 'billing',
        fields: {
          address: 'always',
          phone: 'never',
          organization: 'never',
          name: 'never'
        },
        validation: {
          address: {
            required: 'always'
          }
        },
        defaultValues: defaultValues
      }

      setAddressOptions(options)

      if (initialValues?.address) {
        setAddressComplete(true)
      } else {
        setAddressComplete(false)
      }
    }
  }, [show, initialValues, firstName, lastName])

  const handleAddressChange = (event) => {
    setAddressComplete(event.complete)
    if (event.error) {
      setAddressError(event.error.message)
    } else {
      setAddressError(null)
    }
  }

  const handleSubmit = async (values) => {
    try {
      if (!addressElementRef.current) {
        setAddressError('Address element not initialized')
        return
      }

      const addressValue = await addressElementRef.current.getValue()

      if (addressValue.complete) {
        const businessDetails = {
          address: addressValue.value
        }

        if (values.organization && values.organization.trim() !== '') {
          businessDetails.organization = values.organization.trim()
        } else {
          setAddressError('Organization Name is required')
          return
        }

        if (values.vatId && values.vatId.trim() !== '') {
          businessDetails.vatId = values.vatId.trim()
        }

        businessDetails.isBusinessAccount = true
        onSave(businessDetails)
      } else {
        setAddressError('Please complete your billing address')
      }
    } catch (error) {
      console.error('Error getting address value:', error)
      setAddressError('Error processing address information')
    }
  }

  return (
    <CustomModal show={show} onHide={onHide} aria-labelledby="contained-modal-title-vcenter" centered>
      <div className="p-2">
        <Modal.Header closeButton>
          <Modal.Title>Business Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={{
              vatId: initialValues.vatId || '',
              organization: initialValues.organization || ''
            }}
          >
            {({ handleSubmit, handleChange, values }) => (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Organization Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="organization"
                    type="text"
                    placeholder="Enter your organization name"
                    value={values.organization}
                    onChange={handleChange}
                    required
                  />
                  <ErrorMessage name="organization" component="div" className="validation-error" />
                  <Form.Text className="text-muted">This will appear on your invoice</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    VAT ID / Tax ID <span className="text-muted">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    name="vatId"
                    type="text"
                    placeholder="Enter your VAT or Tax ID if applicable"
                    value={values.vatId}
                    onChange={handleChange}
                  />
                  <ErrorMessage name="vatId" component="div" className="validation-error" />
                  <Form.Text className="text-muted">Will be included on your invoice if provided</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Billing Address <span className="text-danger">*</span>
                  </Form.Label>
                  {addressOptions && (
                    <div className="address-element-container">
                      <AddressElement
                        options={addressOptions}
                        className={styles.StripeElement}
                        onChange={handleAddressChange}
                        onReady={(element) => {
                          addressElementRef.current = element
                        }}
                      />
                    </div>
                  )}
                  {addressError && <div className="validation-error mt-2">{addressError}</div>}
                  <Form.Text className="text-muted">This address will appear on your invoice</Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end mt-4">
                  <Button variant="outline-secondary" className="me-2" onClick={onHide}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={!addressComplete}>
                    Save
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </div>
    </CustomModal>
  )
}

export default BusinessDetailsModal
